// The Activity log: what happened to you in Udal over the last few days, kept in the browser and read
// in Profile → Activity. The web's copy of ParksphereMobileApp/utils/activityLog.js (the same rules,
// so the two keep the same three days and group them the same way); keep the two the same.
//
// 2026-10-28: it used to be a panel along the bottom of the map, held in sessionStorage — so it was
// gone with the tab, and kept everything for as long as the tab lived. As on the phone, the map now
// only shows each entry briefly as it happens (ActivityToast), and the log lives in Profile.
//
// An entry is { msg, at, rawTime, color }:
//   at       when the thing happened
//   rawTime  when it was added here; the toast and the unread dot key off this
//   color    the web's old tint for the message ('green', 'red', ...), kept for the list

export const ACTIVITY_KEEP_MS = 3 * 24 * 60 * 60 * 1000;
// A ceiling as well as a window: a bad day (a reconnect loop, a flapping spot) must not grow the
// stored log without bound.
export const ACTIVITY_MAX = 200;

export const activityKey = (userId) => `activityLog:${userId}`;
export const activitySeenKey = (userId) => `activitySeen:${userId}`;

const isEntry = (e) => e && typeof e.msg === 'string' && Number.isFinite(e.at) && Number.isFinite(e.rawTime);

/** Drops anything malformed or older than the window, and keeps only the newest ACTIVITY_MAX. */
export function pruneActivity(entries, now = Date.now()) {
  if (!Array.isArray(entries)) return [];
  const kept = entries.filter((e) => isEntry(e) && now - e.at <= ACTIVITY_KEEP_MS);
  return kept.length > ACTIVITY_MAX ? kept.slice(kept.length - ACTIVITY_MAX) : kept;
}

/**
 * The stored log plus whatever was added before it finished loading — the engine can report a park
 * in the first second after launch, before storage has answered. Kept in the order they were added.
 */
export function mergeActivity(stored, live, now = Date.now()) {
  const seen = new Set();
  const all = [...(Array.isArray(stored) ? stored : []), ...(Array.isArray(live) ? live : [])]
    .filter((e) => {
      if (!isEntry(e)) return false;
      const id = `${e.rawTime}|${e.msg}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => a.rawTime - b.rawTime);
  return pruneActivity(all, now);
}

const startOfDay = (ms) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };

/**
 * Newest first, in sections by the day it happened: "Today", "Yesterday", then the weekday — which
 * cannot be ambiguous inside a three-day window.
 * @returns {{title: string, data: object[]}[]}
 */
export function groupActivityByDay(entries, now = Date.now()) {
  const today = startOfDay(now);
  const sections = [];
  const byTitle = new Map();
  [...entries].sort((a, b) => b.at - a.at).forEach((e) => {
    const day = startOfDay(e.at);
    const daysAgo = Math.round((today - day) / (24 * 60 * 60 * 1000));
    const title = daysAgo <= 0 ? 'Today'
      : daysAgo === 1 ? 'Yesterday'
        : new Date(e.at).toLocaleDateString(undefined, { weekday: 'long' });
    if (!byTitle.has(title)) {
      const section = { title, data: [] };
      byTitle.set(title, section);
      sections.push(section);
    }
    byTitle.get(title).data.push(e);
  });
  return sections;
}
